import { defaultAllSelection } from "services/constants/selections";
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { Box } from "@mui/material";
import { designColors } from "services/constants/designTokens";
import { FilterProvider, useFilter } from "../../../FilterContext";
import {
  attendanceDepartmentFilter,
  attendanceEmployeeFilter,
} from "../../TeamZoneHeaders";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import DirectionsRunOutlinedIcon from "@mui/icons-material/DirectionsRunOutlined";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import { Status } from "./Status";
import { Typography } from "@mui/material";
import { showToast } from "services/utils/Status";
import moment from "moment";
import {
  setAttendanceShiftName,
  setAttendanceShiftId,
  setAttendanceDate,
} from "services/Redux/TeamZone";
import { Device } from "components/Navbars/DeviceStatus";
import { CardView } from "./CardView";
import GroupsIcon from "@mui/icons-material/Groups";
import EventNoteIcon from "@mui/icons-material/EventNote";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import ParkIcon from "@mui/icons-material/Park";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import BadgeIcon from "@mui/icons-material/Badge";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useFetch } from "services/hooks/useFetch";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import CoffeeIcon from "@mui/icons-material/Coffee";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import OverviewSkeletonGrid from "./OverviewSkeletonGrid";
import sessionHandling from "services/utils/notificationUtils";
import {
  ApprovalDateRangeFilter,
  filterBarPickerSx,
} from "components/CustomComponents/ApprovalDateRangeFilter";
import { FilterSection } from "pages/FilterContext/FilterRow";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { setFilterApi } from "services/Redux/userManagament";
import { useExcelDownload } from "components/CustomComponents/useExcelDownload";

// const STATUS_TYPE_MAP = {
//   total: "overall_status",
//   planned: "planned_status",
//   leave: "leave_status",
//   "week off": "weekoff_status",
// };

const SUBSTATUS_MAP = {
  "On Duty": "onduty",
  "Overtime": "overtime",
  "Permission": "permission",
  "Present": "present",
  "Absent": "absent",
  "Active": "active",
  "Break": "break",
  "Out": "out",
  "Missing": "missing",
  "Late": "late",
  "Early": "early",
  "Only OT": "only_ot",
  "Approved OT": "approved_ot",
  "Pending OT": "pending_ot",
  "Rejected OT": "rejected_ot",
};

export const AttendanceStatus = ({
  setDeviceOffline,
  deviceOffine,
  departmentDetails,
  shiftData,
  selectedComponent,
  colorCode,
  setSelectedComponent,
  setDepartmentApi,
  departmentApi,
  reloadApi,
  setReloadApi,
  setSelectedDepartmentId,
  markFiltersLoaded,
  urlDrivenFilters,
}) => {
  const previousComponentRef = useRef(selectedComponent);
  // Read inside the urlDrivenFilters-sync effect below instead of closing
  // over `selectedComponent` directly — that effect must NOT re-run just
  // because selectedComponent changed (see comment there for why), but
  // still needs its current value. Kept current via a plain render-time
  // assignment rather than its own effect, since an effect would itself lag
  // a commit behind, reintroducing the exact race this ref exists to avoid.
  const selectedComponentRef = useRef(selectedComponent);
  selectedComponentRef.current = selectedComponent;
  const dispatch = useDispatch();
  const { clearFilterState } = useFilter();
  const { downloadExcel, downloadPdf, downloadCsv } = useExcelDownload();
  const [departmentMembers, setDepartmentMembers] = useState([]);
  const { attendanceShiftName, attendanceShiftId, attendanceDate } =
    useSelector((state) => state.teamZoneReducer);
  const { token, domain, userDetails, topbarCompanyId, topbarCompanyName } =
    useSelector((state) => state.tokenReducer);
  const [departmentFilterData, setDepartmentFilterData] = useState([]);
  const [employeeFilterData, setEmployeeFilterData] = useState([]);
  const [departmentName, setDepartmentName] = useState("All Department");
  const [departmentId, setDepartmentId] = useState(String(defaultAllSelection));
  const [departmentList, setDepartmentList] = useState([]);

  const [date, setDate] = useState(attendanceDate);
  const [shiftName, setShiftName] = useState(attendanceShiftName);
  const [shiftId, setShiftId] = useState(attendanceShiftId);
  // status tag value handling
  const [workForceSelection, setWorkForceSelection] = useState("Total");
  const [attendanceSelection, setAttendanceSelection] = useState("");
  const [presentStatusSelection, setPresentStatusSelection] = useState("");
  const [overtimeStatusSelection, setOvertimeStatusSelection] = useState("");

  // Get substatus value based on selection priority
  const getSubstatus = () => {
    let substatusValue = "";

    if (presentStatusSelection !== "") {
      substatusValue =
        SUBSTATUS_MAP[presentStatusSelection] ||
        presentStatusSelection.toLowerCase().replace(/\s+/g, "_");
    } else if (attendanceSelection === "Overtime" && overtimeStatusSelection !== "") {
      substatusValue =
        SUBSTATUS_MAP[overtimeStatusSelection] ||
        overtimeStatusSelection.toLowerCase().replace(/\s+/g, "_");
    } else if (attendanceSelection !== "") {
      substatusValue =
        SUBSTATUS_MAP[attendanceSelection] ||
        attendanceSelection.toLowerCase().replace(/\s+/g, "_");
    }

    return substatusValue;
  };

  // Get scope status value
  const getScopeStatus = (selection) => {
    const map = {
      Total: "total",
      Planned: "planned",
      "Week Off": "weekoff",
      Leave: "leave",
    };
    return map[selection] || selection.toLowerCase();
  };

  const scopeStatus = getScopeStatus(workForceSelection);
  const substatus = getSubstatus();

  const { result: attendanceCounts, setLoad: reCallMemberApi } = useFetch(
    `${domain}get-overall-attendance-count/?shift_id=${shiftId}&date=${date}&department_id=${departmentId}&user_id=${userDetails.id}&status=${scopeStatus}&substatus=${substatus}&company_id=${topbarCompanyId}`,
  );

  const [loading, setLoader] = useState(true);

  useEffect(() => {
    if (attendanceDate && attendanceDate !== date) {
      setDate(attendanceDate);
      setReloadApi(true);
      if (selectedComponent !== "Employee View") {
        setDepartmentApi(true);
      }
      reCallMemberApi(true);
    }
  }, [
    attendanceDate,
    date,
    reCallMemberApi,
    selectedComponent,
    setDepartmentApi,
    setReloadApi,
  ]);

  useEffect(() => {
    if (topbarCompanyId) {
      setDepartmentApi(true);
      reCallMemberApi(true);
      setReloadApi(true);
    }
  }, [reCallMemberApi, setDepartmentApi, setReloadApi, topbarCompanyId]);

  // Reset happens on every manual switch back to the Overview tab, but must
  // defer to urlDrivenFilters (falling back to the same "Total"/"" a plain,
  // un-filtered visit already resolves to) rather than hardcoding "Total" —
  // otherwise a workforce=planned deep link race-loses to this effect. The
  // parent (Attendance/index.js) syncs `selectedComponent` from
  // urlDrivenFilters.component in its own effect, one commit behind this
  // component receiving the new urlDrivenFilters prop, so a link landing on
  // Overview goes through an intermediate commit where selectedComponent is
  // still the previous tab; this effect fires again once selectedComponent
  // catches up, and previously stomped the just-applied selection back to
  // "Total" a beat later. Sourcing the reset from urlDrivenFilters makes
  // that second firing re-apply the same (correct) value instead.
  useEffect(() => {
    if (selectedComponent === "Overview") {
      setDepartmentApi(true);
      clearFilterState();
      setWorkForceSelection(urlDrivenFilters?.workForceSelection || "Total");
      setAttendanceSelection(urlDrivenFilters?.attendanceSelection || "");
      setPresentStatusSelection(urlDrivenFilters?.presentStatusSelection || "");
      setOvertimeStatusSelection(urlDrivenFilters?.overtimeStatusSelection || "");
    }
  }, [clearFilterState, selectedComponent, setDepartmentApi, urlDrivenFilters]);

  useEffect(() => {
    if (selectedComponent === "Employee View") {
      dispatch(setFilterApi(true));
    }
  }, [dispatch, selectedComponent]);

  useEffect(() => {
    if (previousComponentRef.current !== selectedComponent) {
      clearFilterState();
      previousComponentRef.current = selectedComponent;
    }
  }, [clearFilterState, selectedComponent]);

  // Must fire ONLY when urlDrivenFilters (i.e. the URL) actually changes —
  // not on every selectedComponent change. StatBox's onClick (below) sets
  // workForceSelection directly AND flips selectedComponent to "Employee
  // View" in the same batch; since urlDrivenFilters still reflects
  // whatever (or nothing) the page was originally loaded with, this effect
  // re-running off that unrelated selectedComponent change would resync
  // workForceSelection back to the stale URL value — silently reverting
  // the click that was just made until a second, no-op-selectedComponent
  // click let it stick. selectedComponentRef sidesteps that: it's read for
  // the setDepartmentApi decision without being a reactive trigger.
  useEffect(() => {
    if (!urlDrivenFilters) {
      return;
    }

    setWorkForceSelection(urlDrivenFilters.workForceSelection || "Total");
    setAttendanceSelection(urlDrivenFilters.attendanceSelection || "");
    setPresentStatusSelection(urlDrivenFilters.presentStatusSelection || "");
    setOvertimeStatusSelection(urlDrivenFilters.overtimeStatusSelection || "");
    setReloadApi(true);

    if (selectedComponentRef.current !== "Employee View") {
      setDepartmentApi(true);
    }

    reCallMemberApi(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reCallMemberApi, setDepartmentApi, setReloadApi, urlDrivenFilters]);

  useEffect(() => {
    const fetchTeam = async () => {
      setLoader(true);
      try {
        const response = await fetch(
          `${domain}department/details?user_id=${userDetails.id}&company_id=${topbarCompanyId}&shift_id=${shiftId}&date=${date}&department_id=${departmentId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          if (response.status === 202) {
            setDeviceOffline(true);
          } else {
            setDepartmentList(res.departments);
            setDeviceOffline(false);
          }
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error('REQUEST_FAILED');
        }
      } catch (error) {
        console.error('[Index] operation failed:', error);
        showToast("error", "Couldn't load team attendance. Please refresh the page.");
      }
      setLoader(false);
    };
    if (departmentApi) {
      fetchTeam();
      setDepartmentApi(false);
    }
  }, [
    date,
    departmentApi,
    departmentId,
    domain,
    setDepartmentApi,
    setDeviceOffline,
    shiftId,
    token,
    topbarCompanyId,
    userDetails.id,
  ]);

  const totalCards = [
    {
      label: "Total",
      value: attendanceCounts?.overall_count ?? 0,
      color: "#09BD71",
      icon: <GroupsIcon />,
    },
    {
      label: "Planned",
      value: attendanceCounts?.planned_count ?? 0,
      color: "#02AAC8",
      icon: <EventNoteIcon />,
    },
    {
      label: "Week Off",
      value: attendanceCounts?.weekoff_count ?? 0,
      color: "#3F51B5",
      icon: <BeachAccessIcon />,
    },
    {
      label: "Leave",
      value: attendanceCounts?.leave_count ?? 0,
      color: "#F5A623",
      icon: <ParkIcon />,
    },
  ].map((item) => {
    const match = colorCode.find((c) => c.label === item.label);

    return {
      ...item,
      color: item.label === "Total" ? item.color : match?.color || "#ccc",
    };
  });

  const attendanceCards = [
    {
      label: "Present",
      value: attendanceCounts?.present_count ?? 0,
      color: "#09BD71",
      icon: <TaskAltIcon />,
    },
    {
      label: "Absent",
      value: attendanceCounts?.absent_count ?? 0,
      color: "#E53935",
      icon: <HighlightOffIcon />,
    },
  ].map((item) => {
    const match = colorCode.find((c) => c.label === item.label);
    return {
      ...item,
      color: item.label === "Total" ? item.color : match?.color || "#ccc",
    };
  });

  const workStatusCards = [
    {
      label: "Overtime",
      value: attendanceCounts?.overtime_count ?? 0,
      color: "#8A8A5C",
      icon: <DirectionsRunIcon />,
    },
    {
      label: "On Duty",
      value: attendanceCounts?.onduty_count ?? 0,
      color: "#1F8A70",
      icon: <BadgeIcon />,
    },
    {
      label: "Permission",
      value: attendanceCounts?.permission_count ?? 0,
      color: "#8B198D",
      icon: <ScheduleIcon />,
    },
  ].map((item) => {
    const match = colorCode.find((c) => c.label === item.label);

    return {
      ...item,
      color: item.label === "Total" ? item.color : match?.color || "#ccc",
    };
  });

  const presentStatus = [
    {
      label: "Active",
      value: attendanceCounts?.active_count ?? 0,
      color: "#09BD71",
      icon: <SentimentSatisfiedAltIcon />,
    },
    {
      label: "Break",
      value: attendanceCounts?.break_count ?? 0,
      color: "#EC00F1",
      icon: <CoffeeIcon />,
    },
    {
      label: "Out",
      value: attendanceCounts?.out_count ?? 0,
      color: "#8A8A5C",
      icon: <DirectionsRunIcon />,
    },
  ].map((item) => {
    const match = colorCode.find((c) => c.label === item.label);

    return {
      ...item,
      color: item.label === "Active" ? item.color : match?.color || "#ccc",
    };
  });

  const presentStatusWithAnomalies = [
    {
      label: "Missing",
      value: attendanceCounts?.missing_count ?? 0,
      color: "#09BD71",
      icon: <PersonOffOutlinedIcon />,
    },
    {
      label: "Only OT",
      value: attendanceCounts?.only_ot_count ?? 0,
      color: "#5ebbf8",
      icon: <MoreTimeIcon />,
    },
    {
      label: "Late",
      value: attendanceCounts?.late_count ?? 0,
      color: "#EC00F1",
      icon: <AccessTimeOutlinedIcon />,
    },
    {
      label: "Early",
      value: attendanceCounts?.early_exit_count ?? 0,
      color: "#8A8A5C",
      icon: <DirectionsRunOutlinedIcon />,
    },
  ].map((item) => {
    const match = colorCode.find((c) => c.label === item.label);

    return {
      ...item,
      color: match?.color || item.color,
    };
  });

  const overtimeStatusBasedCard = [
    {
      label: "Approved OT",
      value: attendanceCounts?.approved_ot_count ?? 0,
      color: "#09BD71",
      icon: <TaskAltIcon />,
    },
    {
      label: "Pending OT",
      value: attendanceCounts?.pending_ot_count ?? 0,
      color: "#EC00F1",
      icon: <MoreTimeIcon />,
    },
    {
      label: "Rejected OT",
      value: attendanceCounts?.rejected_ot_count ?? 0,
      color: "#8A8A5C",
      icon: <PendingActionsIcon />,
    },
  ].map((item) => {
    const match = colorCode.find((c) => c.label === item.label);

    return {
      ...item,
      color: item.label === "Early" ? item.color : match?.color || "#ccc",
    };
  });

  const handleFilteredDataChange = useCallback((filteredData) => {
    setDepartmentFilterData(filteredData);
  }, []);

  const handleFilteredDataChangeForEmployee = useCallback((filteredData) => {
    setEmployeeFilterData(filteredData);
  }, []);

  const handleDepartmentName = (event, data) => {
    if (data) {
      setDepartmentId(data?.id);
      setSelectedDepartmentId(data?.id);
      setDepartmentName(data?.label);
      reCallMemberApi(true);
      setReloadApi(true);
      if (selectedComponent !== "Employee View") {
        setDepartmentApi(true);
      }
    } else {
      setDepartmentId("");
      setSelectedDepartmentId("");
      setDepartmentName("");
    }
  };

  const handleDateChange = (value) => {
    if (value !== "") {
      dispatch(setAttendanceDate(value));
      localStorage.setItem("attendanceDate", value);
      setDate(value);
      setReloadApi(true);
      if (selectedComponent !== "Employee View") {
        setDepartmentApi(true);
      }
      reCallMemberApi(true);
    } else {
      setDate(value);
    }
  };

  const handleShiftname = (event, data) => {
    if (data) {
      setShiftName(data?.label);
      setShiftId(data?.id);
      setReloadApi(true);
      if (selectedComponent !== "Employee View") {
        setDepartmentApi(true);
      }
      dispatch(setAttendanceShiftId(data?.id));
      dispatch(setAttendanceShiftName(data?.label));
      localStorage.setItem("attendanceShiftId", data?.id);
      localStorage.setItem("attendanceShiftName", data?.label);
      reCallMemberApi(true);
    }
  };

  const StatBox = ({
    label,
    value,
    color,
    icon,
    selectedData,
    setSelectedData,
    setReloadApi,
    setSelectedComponent,
    updateStatus,
  }) => {
    const isSelected = selectedData === label;

    return (
      <Box
        onClick={() => {
          setSelectedData(label);
          setReloadApi(true);
          updateStatus();
          if (selectedComponent !== "Employee View") {
            setSelectedComponent("Employee View");
          }
        }}
        sx={{
          flex: 1,
          minWidth: { xs: 96, sm: 140 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          cursor: "pointer",
          py: { xs: 0.75, sm: 0.5 },
          borderBottom: isSelected
            ? `3px solid ${color}`
            : "3px solid transparent",
          transition: "all 0.2s ease",
        }}
      >
        {icon && <Box sx={{ color }}>{icon}</Box>}
        <Typography variant="body2">{label}</Typography>
        <Typography sx={{ color, fontWeight: 1200, fontSize: 22 }}>
          {value ?? 0}
        </Typography>
      </Box>
    );
  };

  const AttendanceFilters = (
    <FilterSection
      filterLabel="Filters"
      filters={[
        {
          width: 300,
          props: {
            dataList: departmentDetails.map((obj) => ({
              ...obj,
              label: obj.department_name,
            })),
            selectedValue: departmentName,
            updateDataFunction: handleDepartmentName,
            Icons: PersonOutlineIcon,
            staticLabel: "Department",
          },
        },
        {
          width: 300,
          props: {
            dataList: shiftData?.map((obj) => ({
              ...obj,
              label: obj.name,
            })),
            selectedValue: shiftName,
            updateDataFunction: handleShiftname,
            Icons: PersonOutlineIcon,
            staticLabel: "Shift",
          },
        },
        {
          width: 260,
          component: (
            <ApprovalDateRangeFilter
              singleDatePicker
              startDate={date}
              endDate={date}
              onChange={(pickedDate) => {
                const nextDate = moment(pickedDate).format("YYYY-MM-DD");
                // Ignore the picker's initial on-mount report of the same date.
                if (nextDate !== date) handleDateChange(nextDate);
              }}
              showToday={true}
              includeCurrentDate={true}
              allowFutureDate={false}
              staticLabel="Date :"
              sx={filterBarPickerSx}
            />
          ),
        },
      ]}
    />
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      Date: date ? moment(date).format("DD/MM/YYYY") : "N/A",
      Department: departmentName || "All",
      Shift: shiftName || "All",
      Total: attendanceCounts?.overall_count ?? 0,
      Planned: attendanceCounts?.planned_count ?? 0,
      "Week Off": attendanceCounts?.weekoff_count ?? 0,
      Leave: attendanceCounts?.leave_count ?? 0,
      Present: attendanceCounts?.present_count ?? 0,
      Absent: attendanceCounts?.absent_count ?? 0,
      Overtime: attendanceCounts?.overtime_count ?? 0,
      "On Duty": attendanceCounts?.onduty_count ?? 0,
      Permission: attendanceCounts?.permission_count ?? 0,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [
      attendanceCounts,
      date,
      departmentName,
      shiftName,
      topbarCompanyName,
    ],
  );

  const handleOverviewDownload = useCallback(
    async (exportRows, onProgress, downloadFormat = "excel") => {
      const rows = Array.isArray(exportRows) ? exportRows : [];
      const sumField = (field) =>
        rows.reduce((total, row) => {
          const value = Number(row?.[field] ?? 0);
          return total + (Number.isNaN(value) ? 0 : value);
        }, 0);

      const summaryRow = {
        rowType: "grand_total",
        department_name: "Total",
        staff_planned_count: sumField("staff_planned_count"),
        staff_present_count: sumField("staff_present_count"),
        staff_overtime_count: sumField("staff_overtime_count"),
        staff_missing_count: sumField("staff_missing_count"),
        staff_absent_count: sumField("staff_absent_count"),
        staff_late_early: sumField("staff_late_early"),
      };

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadPdf
          : downloadFormat === "csv"
            ? downloadCsv
            : downloadExcel;

      await exportToFormat(
        [...rows, summaryRow],
        attendanceDepartmentFilter,
        "Attendance Record",
        {},
        reportMetadata,
        [],
      );

      if (typeof onProgress === "function") {
        onProgress(100);
      }
    },
    [
      downloadCsv,
      downloadExcel,
      downloadPdf,
      reportMetadata,
    ],
  );

  return deviceOffine ? (
    <>
      <Mui.Box sx={{ px: 3, pt: 2 }}>
        <Mui.Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {AttendanceFilters}
          {/* Right Side Icons */}
          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
            {selectedComponent === "Employee View" ? (
              <CustomFilter
                title={"Attendance Record"}
                filterHeaders={attendanceEmployeeFilter}
                data={departmentMembers}
                filteredData={employeeFilterData}
                onFilteredDataChange={handleFilteredDataChangeForEmployee}
                screenAccess={true}
                showtitle_count={false}
                downloadFormats={["excel", "pdf", "csv"]}
                includeMetadata={true}
                metadata={reportMetadata}
              />
            ) : (
              <CustomFilter
                title={"Attendance Record"}
                filterHeaders={attendanceDepartmentFilter}
                data={departmentList}
                filteredData={departmentFilterData}
                onFilteredDataChange={handleFilteredDataChange}
                screenAccess={true}
                showtitle_count={false}
                downloadFormats={["excel", "pdf", "csv"]}
                includeMetadata={true}
                metadata={reportMetadata}
                customDownloadHandler={handleOverviewDownload}
              />
            )}
          </Box>
        </Mui.Box>
      </Mui.Box>
      <Device />
    </>
  ) : (
    <React.Fragment>
      {/* Integrated CustomFilter with filters and count */}
      {selectedComponent === "Employee View" ? (
        <CustomFilter
          title={"Attendance Record"}
          filterHeaders={attendanceEmployeeFilter}
          data={departmentMembers}
          filteredData={employeeFilterData}
          onFilteredDataChange={handleFilteredDataChangeForEmployee}
          screenAccess={true}
          showtitle_count={true}
          viewFilter={true}
          showFilter={true}
          filters={AttendanceFilters}
          downloadFormats={["excel", "pdf", "csv"]}
          includeMetadata={true}
          metadata={reportMetadata}
        />
      ) : (
        <CustomFilter
          title={"Attendance Record"}
          filterHeaders={attendanceDepartmentFilter}
          data={departmentList}
          filteredData={departmentFilterData}
          onFilteredDataChange={handleFilteredDataChange}
          screenAccess={true}
          showtitle_count={true}
          showFilter={true}
          filters={AttendanceFilters}
          showSaveButton={false}
          downloadFormats={["excel", "pdf", "csv"]}
          includeMetadata={true}
          metadata={reportMetadata}
          customDownloadHandler={handleOverviewDownload}
        />
      )}

      {/* Status Dashboard - Workforce, Attendance, Work Status */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 2,
          p: { xs: 1, sm: 2 },
          alignItems: "stretch",
          minWidth: 0,
        }}
      >
        <Box sx={{ flex: 2, minWidth: 0 }}>
          <Box
            sx={{
              border: "1px solid #ddd",
              borderRadius: 2,
              pl: { xs: 1, sm: 2 },
              pr: { xs: 1, sm: 2 },
              pb: 1,
              pt: 1,
              height: "100%",
              minWidth: 0,
            }}
          >
            <Typography sx={{ padding: 0.2, color: designColors.slate600 }}>
              Workforce
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1, sm: 2 },
                flexWrap: "wrap",
                alignItems: "stretch",
                background: "#fff",
                px: { xs: 1, sm: 2 },
                py: 0.5,
                borderRadius: 2,
              }}
            >
              {totalCards.map((card, index) => (
                <StatBox
                  key={index}
                  label={card.label}
                  value={card.value}
                  color={card.color}
                  icon={card.icon}
                  selectedData={workForceSelection}
                  setSelectedData={setWorkForceSelection}
                  setReloadApi={setReloadApi}
                  setSelectedComponent={setSelectedComponent}
                  updateStatus={() => {
                    reCallMemberApi(true);
                    setAttendanceSelection("");
                    setPresentStatusSelection("");
                    setOvertimeStatusSelection("");
                  }}
                />
              ))}
            </Box>
            <Divider sx={{ my: 0.5 }} />
            {/* Row 2 */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: { xs: 1.5, sm: 3 },
                flexWrap: "wrap",
              }}
            >
              {/* Attendance */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ padding: 0.2, color: designColors.slate600 }}>
                  Attendance
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 1, sm: 2 },
                    flexWrap: "wrap",
                    background: "#fff",
                    px: { xs: 1, sm: 2 },
                    py: 0.5,
                    borderRadius: 2,
                  }}
                >
                  {attendanceCards.map((card, index) => (
                    <StatBox
                      key={index}
                      {...card}
                      selectedData={attendanceSelection}
                      setSelectedData={setAttendanceSelection}
                      setReloadApi={setReloadApi}
                      setSelectedComponent={setSelectedComponent}
                      updateStatus={() => {
                        reCallMemberApi(true);
                        setPresentStatusSelection("");
                        setOvertimeStatusSelection("");
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Work Status */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ padding: 0.2, color: designColors.slate600 }}>
                  Work Status
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 1, sm: 2 },
                    flexWrap: "wrap",
                    background: "#fff",
                    px: { xs: 1, sm: 2 },
                    py: 0.5,
                    borderRadius: 2,
                  }}
                >
                  {workStatusCards.map((card, index) => (
                    <StatBox
                      key={index}
                      {...card}
                      selectedData={attendanceSelection}
                      setSelectedData={setAttendanceSelection}
                      setReloadApi={setReloadApi}
                      setSelectedComponent={setSelectedComponent}
                      updateStatus={() => {
                        reCallMemberApi(true);
                        setPresentStatusSelection("");
                        setOvertimeStatusSelection("");
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Overtime Status */}
            {attendanceSelection === "Overtime" &&
              (overtimeStatusSelection === "" ||
                overtimeStatusSelection === "Approved OT" ||
                overtimeStatusSelection === "Pending OT" ||
                overtimeStatusSelection === "Rejected OT" ||
                overtimeStatusSelection === "Ineligible OT") && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <Typography sx={{ padding: 0.2, color: designColors.slate600 }}>
                    Overtime
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: 1, sm: 2 },
                      flexWrap: "wrap",
                      alignItems: "stretch",
                      background: "#fff",
                      px: { xs: 1, sm: 2 },
                      py: 0.5,
                      borderRadius: 2,
                    }}
                  >
                    {overtimeStatusBasedCard.map((card, index) => (
                      <StatBox
                        key={index}
                        label={card.label}
                        value={card.value}
                        color={card.color}
                        icon={card.icon}
                        selectedData={overtimeStatusSelection}
                        setSelectedData={setOvertimeStatusSelection}
                        setReloadApi={setReloadApi}
                        setSelectedComponent={setSelectedComponent}
                        updateStatus={() => {
                          reCallMemberApi(true);
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}

            {/* Present Status & Anomalies */}
            {attendanceSelection === "Present" &&
              (presentStatusSelection === "" ||
                presentStatusSelection === "Active" ||
                presentStatusSelection === "Break" ||
                presentStatusSelection === "Out" ||
                presentStatusSelection === "Missing" ||
                presentStatusSelection === "Only OT" ||
                presentStatusSelection === "Late" ||
                presentStatusSelection === "Early") &&
              selectedComponent === "Employee View" && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      gap: { xs: 1.5, sm: 3 },
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Present Status */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ padding: 0.2, color: designColors.slate600 }}>
                        Present Status
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          gap: { xs: 1, sm: 2 },
                          flexWrap: "wrap",
                          background: "#fff",
                          px: { xs: 1, sm: 2 },
                          py: 0.5,
                          borderRadius: 2,
                        }}
                      >
                        {presentStatus.map((card, index) => (
                          <StatBox
                            key={index}
                            {...card}
                            selectedData={presentStatusSelection}
                            setSelectedData={setPresentStatusSelection}
                            setReloadApi={setReloadApi}
                            setSelectedComponent={setSelectedComponent}
                            updateStatus={() => {
                              reCallMemberApi(true);
                            }}
                          />
                        ))}
                      </Box>
                    </Box>

                    {/* Anomalies */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ padding: 0.2, color: designColors.slate600 }}>
                        Anomalies
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          gap: { xs: 1, sm: 2 },
                          flexWrap: "wrap",
                          background: "#fff",
                          px: { xs: 1, sm: 2 },
                          py: 0.5,
                          borderRadius: 2,
                        }}
                      >
                        {presentStatusWithAnomalies.map((card, index) => (
                          <StatBox
                            key={index}
                            {...card}
                            selectedData={presentStatusSelection}
                            setSelectedData={setPresentStatusSelection}
                            setReloadApi={setReloadApi}
                            setSelectedComponent={setSelectedComponent}
                            updateStatus={() => {
                              reCallMemberApi(true);
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
          </Box>
        </Box>

        {/* Operational Status */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              border: "1px solid #ddd",
              borderRadius: 2,
              p: 1,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography fontWeight={600} mb={1}>
              Operational Status
            </Typography>

            {/* Content container */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-evenly",
              }}
            >
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={
                    ((attendanceCounts?.planned_count ?? 0) /
                      (attendanceCounts?.overall_count ?? 1)) *
                    100
                  }
                  sx={{
                    height: 12,
                    borderRadius: 5,
                    mt: 0.5,
                    backgroundColor: "#E5E7EB",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#4A647B",
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 0.5,
                    mt: 0.5,
                    alignItems: "center",
                  }}
                >
                  <b style={{ fontSize: "18px" }}>
                    {attendanceCounts?.planned_count ?? 0}
                  </b>
                  <Typography color={designColors.slate600}>Planned / </Typography>
                  <b style={{ fontSize: "18px" }}>
                    {attendanceCounts?.overall_count ?? 0}
                  </b>
                  <Typography color={designColors.slate600}>Total</Typography>
                </Typography>
              </Box>

              <Box>
                <LinearProgress
                  variant="determinate"
                  value={
                    ((attendanceCounts?.present_count ?? 0) /
                      (attendanceCounts?.planned_count ?? 1)) *
                    100
                  }
                  sx={{
                    height: 12,
                    borderRadius: 5,
                    mt: 0.5,
                    backgroundColor: "#E5E7EB",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#17B26A",
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 0.5,
                    mt: 0.5,
                    alignItems: "center",
                  }}
                >
                  <b style={{ fontSize: "18px" }}>
                    {attendanceCounts?.present_count ?? 0}
                  </b>
                  <Typography color={designColors.slate600}>Present / </Typography>
                  <b style={{ fontSize: "18px" }}>
                    {attendanceCounts?.planned_count ?? 0}
                  </b>
                  <Typography color={designColors.slate600}>Planned</Typography>
                </Typography>
              </Box>

              {(attendanceSelection === "Present" ||
                attendanceSelection === "Active" ||
                attendanceSelection === "Break" ||
                attendanceSelection === "Out" ||
                attendanceSelection === "Missing" ||
                attendanceSelection === "Late" ||
                attendanceSelection === "Early") &&
                selectedComponent === "Employee View" && (
                  <Box>
                    <LinearProgress
                      variant="determinate"
                      value={
                        ((attendanceCounts?.active_count ?? 0) /
                          (attendanceCounts?.present_count ?? 1)) *
                        100
                      }
                      sx={{
                        height: 12,
                        borderRadius: 5,
                        mt: 0.5,
                        backgroundColor: "#E5E7EB",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#6FB79E",
                        },
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 0.5,
                        mt: 0.5,
                        alignItems: "center",
                      }}
                    >
                      <b style={{ fontSize: "18px" }}>
                        {attendanceCounts?.active_count ?? 0}
                      </b>
                      <Typography color={designColors.slate600}>Active / </Typography>
                      <b style={{ fontSize: "18px" }}>
                        {attendanceCounts?.present_count ?? 0}
                      </b>
                      <Typography color={designColors.slate600}>Present</Typography>
                    </Typography>
                  </Box>
                )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Content Views */}
      {loading && selectedComponent === "Overview" ? (
        <OverviewSkeletonGrid />
      ) : (
        <Box>
          {selectedComponent === "Employee View" ? (
            <FilterProvider
              domain={domain}
              userDetails={userDetails}
              subModuleId={152}
            >
              <Status
                shiftId={shiftId}
                date={date}
                departmentId={departmentId}
                reloadApi={reloadApi}
                setReloadApi={setReloadApi}
                setDepartmentMembers={setDepartmentMembers}
                departmentMembers={departmentMembers}
                employeeFilterData={employeeFilterData}
                setEmployeeFilterData={setEmployeeFilterData}
                setDepartmentApi={setDepartmentApi}
                workForceSelection={workForceSelection}
                attendanceSelection={attendanceSelection}
                presentStatusSelection={presentStatusSelection}
                overtimeStatusSelection={overtimeStatusSelection}
                reCallMemberApi={reCallMemberApi}
                markFiltersLoaded={markFiltersLoaded}
              />
            </FilterProvider>
          ) : (
            <FilterProvider
              domain={domain}
              userDetails={userDetails}
              subModuleId={152}
            >
              <CardView
                departmentList={departmentFilterData}
                setSelectedComponent={setSelectedComponent}
                setDepartmentIdValue={setDepartmentId}
                setDepartmentNameValue={setDepartmentName}
                setDepartmentApi={setDepartmentApi}
              />
            </FilterProvider>
          )}
        </Box>
      )}
    </React.Fragment>
  );
};
